// A document's CEISA response history (revamp PR 7a): the Doc Pabean row's clock button and its dialog. Same request
// (GET get-history-status on open), demo branch, close-on-error and lead-time maths as the Mantine modal it replaces;
// the dialog is a Lumen Dialog, the timeline a token-styled list, the toasts sonner.
import { CircleCheck, Clock, Hourglass, Loader2 } from 'lucide-react';
import type * as React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { DataTableMono, DataTableRowAction } from '@/components/data-table';
import { Badge } from '@/components/lumen/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/lumen/dialog';
import { cn } from '@/lib/utils';
import { useLanguage } from '@contexts/LanguageContext';
import axiosInstance from '@libs/axios';
import { getDemoStatusHistory, isDemoUser } from '../../app/(dashboard)/doc-pabean/apis/demo-data';

interface StatusHistoryItem {
  responseName: string;
  responseTime: string;
  leadTime: number;
  fileURL?: string;
}

interface StatusHistoryData {
  drafterName: string;
  companyName: string;
  ajuNo: string;
  items: StatusHistoryItem[];
}

interface StatusHistoryModalProps {
  opened: boolean;
  onClose: () => void;
  ajuNo: string;
  totalItem?: number;
  shipperCountry?: string;
}

/** Seconds → `2d 3h`, `5h 12m`, `4m 9s` or `0s`. */
const formatLeadTime = (seconds: number): string => {
  if (seconds === 0) return '0s';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
  return `${remainingSeconds}s`;
};

const formatResponseTime = (dateTime: string): { date: string; time: string } => {
  const date = new Date(dateTime);
  return {
    date: date.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
  };
};

/** The old per-status chip colours as #15 tones: draft neutral, submitted violet, UMUM ok, NPE err, SPPB info. */
const STATUS_TONE: Record<string, 'neutral' | 'ai' | 'ok' | 'err' | 'info'> = {
  DRAFT: 'neutral',
  'DRAFT SUBMITTED': 'ai',
  UMUM: 'ok',
  NPE: 'err',
  SPPB: 'info',
};
const toneOf = (status: string) => STATUS_TONE[status.toUpperCase()] ?? 'neutral';

/** The bullet's ring and icon colour per tone. */
const BULLET: Record<ReturnType<typeof toneOf>, string> = {
  neutral: 'border-line-strong bg-surface-2 text-fg-2',
  ai: 'border-ai/40 bg-ai/10 text-ai-ink',
  ok: 'border-ok/40 bg-ok/10 text-ok-ink',
  err: 'border-err/40 bg-err/10 text-err-ink',
  info: 'border-label/40 bg-label/10 text-info-ink',
};

const formatAjuNumber = (value: string) => {
  if (!value) return '-';
  const raw = value.replace(/-/g, '');
  if (raw.length <= 6) return raw;
  if (raw.length <= 12) return `${raw.slice(0, 6)}-${raw.slice(6)}`;
  if (raw.length <= 20) return `${raw.slice(0, 6)}-${raw.slice(6, 12)}-${raw.slice(12)}`;
  return `${raw.slice(0, 6)}-${raw.slice(6, 12)}-${raw.slice(12, 20)}-${raw.slice(20)}`;
};

export default function StatusHistoryModal({
  opened,
  onClose,
  ajuNo,
  totalItem,
  shipperCountry,
}: StatusHistoryModalProps) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<StatusHistoryData | null>(null);

  // as the old modal, one fetch per open and per AJU.
  useEffect(() => {
    const fetchStatusHistory = async () => {
      if (!ajuNo || !opened) return;
      setLoading(true);
      try {
        if (isDemoUser()) {
          setData(getDemoStatusHistory(ajuNo));
          setLoading(false);
          return;
        }
        const response = await axiosInstance.get(`/api/v1/ceisa/uploads/get-history-status?ajuNo=${ajuNo}`);
        if (response.data?.success) {
          setData(response.data.data);
        } else {
          toast.error(t('statushistory.errFetch'));
          onClose();
        }
      } catch (error) {
        console.error('Error fetching status history:', error);
        toast.error(t('statushistory.errGeneric'));
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchStatusHistory();
  }, [opened, ajuNo]);

  // Reset data when modal closes
  useEffect(() => {
    if (!opened) {
      setData(null);
    }
  }, [opened]);

  const row = (label: string, value: React.ReactNode) => (
    <div className="flex items-center justify-between gap-3">
      <span className="font-medium text-fg text-xs">{label}</span>
      {value}
    </div>
  );

  return (
    <Dialog open={opened} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="grid size-6 place-items-center rounded-control bg-surface-2 text-fg">
              <Clock aria-hidden className="size-3.5" />
            </span>
            {t('statushistory.title')}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="flex items-center justify-center gap-2 py-6 text-fg-muted text-xs">
            <Loader2 aria-hidden className="size-5 animate-spin text-label motion-reduce:animate-none" />
            {t('statushistory.loading')}
          </p>
        ) : data ? (
          <div className="grid gap-3">
            <div className="grid gap-1 rounded-card border border-line bg-surface-2 p-3">
              {row(
                t('statushistory.document'),
                <DataTableMono className="font-semibold text-fg text-xs">{formatAjuNumber(data.ajuNo)}</DataTableMono>
              )}
              <div className="flex items-center justify-between gap-3 text-fg-muted text-xs">
                <span>{data.companyName}</span>
                <span>
                  {t('statushistory.by')} {data.drafterName}
                </span>
              </div>
              {(totalItem || shipperCountry) && (
                <div className="mt-1 grid gap-1 border-line border-t pt-2">
                  {totalItem
                    ? row(
                        t('statushistory.totalItems'),
                        <Badge variant="neutral">
                          {totalItem} {totalItem === 1 ? t('statushistory.item') : t('statushistory.items')}
                        </Badge>
                      )
                    : null}
                  {shipperCountry &&
                    row(t('statushistory.country'), <Badge variant="info">{shipperCountry.toUpperCase()}</Badge>)}
                </div>
              )}
            </div>

            <ol className="relative grid">
              {data.items.map((item, index) => {
                const { date, time } = formatResponseTime(item.responseTime);
                const tone = toneOf(item.responseName);
                const last = index === data.items.length - 1;
                return (
                  // biome-ignore lint/suspicious/noArrayIndexKey: the history is a fixed list; names can repeat.
                  <li key={index} className="relative grid grid-cols-[20px_1fr] gap-x-3 pb-3 last:pb-0">
                    {!last && <span aria-hidden className="absolute top-5 bottom-0 left-[9px] w-[3px] bg-line" />}
                    <span
                      className={cn(
                        'relative z-[1] mt-0.5 grid size-5 place-items-center rounded-full border [&_svg]:size-3',
                        BULLET[tone]
                      )}
                    >
                      {index === 0 && item.responseName.toUpperCase() === 'DRAFT' ? <Hourglass /> : <CircleCheck />}
                    </span>
                    <div className="grid gap-0.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-13 text-fg leading-tight">{item.responseName}</span>
                        {item.leadTime > 0 && <Badge variant={tone}>{formatLeadTime(item.leadTime)}</Badge>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-fg-muted text-xs">
                          {date} • {time}
                        </span>
                        {item.fileURL && <Badge variant="info">{t('statushistory.file')}</Badge>}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>

            <div className="rounded-card border border-line bg-surface-2 p-3">
              {row(
                t('statushistory.totalTime'),
                <Badge variant="info">
                  {data.items.length > 0
                    ? formatLeadTime(data.items.reduce((total, item) => total + item.leadTime, 0))
                    : '0s'}
                </Badge>
              )}
            </div>
          </div>
        ) : (
          <p className="py-6 text-center text-fg-muted text-xs">{t('statushistory.noData')}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** The row's clock button — stateless, owned by the table. */
export function StatusHistoryIconButton({
  onOpen,
}: {
  onOpen: () => void;
}) {
  const { t } = useLanguage();

  return (
    <DataTableRowAction label={t('statushistory.viewResponHistory')} tone="warn" data-action="history" onClick={onOpen}>
      <Clock />
    </DataTableRowAction>
  );
}
